import React from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInstagram,  faTiktok } from '@fortawesome/free-brands-svg-icons';
import { motion } from 'framer-motion';
import Logo from './Logo';
import { useTranslation } from 'react-i18next';
import { useLocale } from '../context/LocaleContext';

const Footer: React.FC = () => {
  const { t } = useTranslation();
  const { localePath } = useLocale();

  return (
    <footer className="relative z-10 text-text-light dark:text-text-dark py-8 pb-24 md:pb-8 mt-auto">
      <div className="mx-auto px-4 max-w-[min(80vw,1400px)]">

        {/* Mobile layout */}
        <div className="md:hidden text-center text-text-dark dark:text-text-light">
          <motion.div whileHover={{ scale: 1.1 }} className="inline-block">
            <Logo switchable={false} />
          </motion.div>
          <p className="text-sm mt-2 mb-6">Joyitas Concientes</p>

          <nav className="flex flex-wrap justify-center gap-x-4 gap-y-2 text-sm mb-4">
            <Link to={localePath('/')}>{t('nav.home')}</Link>
            <Link to={localePath('/shop')}>{t('nav.shop')}</Link>
            <Link to={localePath('/about')}>{t('nav.about')}</Link>
            <Link to={localePath('/contact')}>{t('nav.contact')}</Link>
          </nav>

          <nav className="flex flex-wrap justify-center gap-x-4 gap-y-2 text-sm mb-6">
            <Link to={localePath('/categories/:category', { category: 'earrings-and-hoops' })}>{t('footer.earringsHoops')}</Link>
            <Link to={localePath('/categories/:category', { category: 'rings' })}>{t('footer.rings')}</Link>
            <Link to={localePath('/categories/:category', { category: 'unique-finds' })}>{t('footer.uniqueFinds')}</Link>
          </nav>

          <div className="flex justify-center gap-6 mb-6">
            <a href="https://www.instagram.com/camilaparente___/" target="_blank" rel="noopener noreferrer" aria-label="Instagram">
              <FontAwesomeIcon icon={faInstagram} size="lg" />
            </a>
            <a href="https://www.tiktok.com/@joyitas.conscientes?_t=8s4VFDx4qrl&_r=1" target="_blank" rel="noopener noreferrer" aria-label="TikTok">
              <FontAwesomeIcon icon={faTiktok} size="lg" />
            </a>
          </div>

          <nav className="flex flex-wrap justify-center gap-x-3 gap-y-1 text-xs text-text-dark/70 dark:text-text-light/70 mb-4">
            <Link to={`${localePath('/legal')}#shipping`}>{t('footer.shippingPolicy')}</Link>
            <span>·</span>
            <Link to={`${localePath('/legal')}#returns`}>{t('footer.returnsPolicy')}</Link>
            <span>·</span>
            <Link to={`${localePath('/legal')}#terms`}>{t('footer.termsConditions')}</Link>
            <span>·</span>
            <Link to={`${localePath('/legal')}#privacy`}>{t('footer.privacyPolicy')}</Link>
          </nav>

          <p className="text-xs text-text-dark/50 dark:text-text-light/50">
            {t('footer.copyright', { year: new Date().getFullYear() })}
          </p>
        </div>

        {/* Desktop layout */}
        <div className="hidden md:block">
          <div className="grid grid-cols-5 gap-8">
            {/* Logo Section */}
            <div className="flex flex-col items-start">
              <motion.div whileHover={{ scale: 1.1 }}>
                <Logo switchable={false} />
              </motion.div>
              <p className="text-sm text-text-dark dark:text-text-light mt-4">Joytias Concientes</p>
            </div>

            {/* Main Links Section */}
            <div className="flex flex-col items-start">
              <h3 className="text-lg font-semibold mb-4 text-text-dark dark:text-text-light">{t('footer.quickLinks')}</h3>
              <nav className="flex flex-col space-y-2 text-left text-text-dark dark:text-text-light">
                <motion.div whileHover={{ scale: 1.1 }}>
                  <Link to={localePath('/')} className="transition-transform">{t('nav.home')}</Link>
                </motion.div>
                <motion.div whileHover={{ scale: 1.1 }}>
                  <Link to={localePath('/shop')} className="transition-transform">{t('nav.shop')}</Link>
                </motion.div>
                <motion.div whileHover={{ scale: 1.1 }}>
                  <Link to={localePath('/about')} className="transition-transform">{t('nav.about')}</Link>
                </motion.div>
                <motion.div whileHover={{ scale: 1.1 }}>
                  <Link to={localePath('/contact')} className="transition-transform">{t('nav.contact')}</Link>
                </motion.div>
              </nav>
            </div>

            {/* Collections Section */}
            <div className="flex flex-col items-start">
              <h3 className="text-lg font-semibold mb-4 text-text-dark dark:text-text-light">{t('footer.categories')}</h3>
              <nav className="flex flex-col space-y-2 text-left text-text-dark dark:text-text-light">
                <motion.div whileHover={{ scale: 1.1 }}>
                  <Link to={localePath('/categories/:category', { category: 'unique-finds' })} className="transition-transform">{t('footer.uniqueFinds')}</Link>
                </motion.div>
                <motion.div whileHover={{ scale: 1.1 }}>
                  <Link to={localePath('/categories/:category', { category: 'earrings-and-hoops' })} className="transition-transform">{t('footer.earringsHoops')}</Link>
                </motion.div>
                <motion.div whileHover={{ scale: 1.1 }}>
                  <Link to={localePath('/categories/:category', { category: 'rings' })} className="transition-transform">{t('footer.rings')}</Link>
                </motion.div>
              </nav>
            </div>

            {/* Social Links Section */}
            <div className="flex flex-col items-start">
              <h3 className="text-lg font-semibold mb-4 text-text-dark dark:text-text-light">{t('footer.socials')}</h3>
              <div className="flex gap-6 text-text-dark dark:text-text-light">
                <motion.a whileHover={{ scale: 1.1 }} href="https://www.instagram.com/camilaparente___/" target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="transition-transform">
                  <FontAwesomeIcon icon={faInstagram} size="lg" />
                </motion.a>
                <motion.a whileHover={{ scale: 1.1 }} href="https://www.tiktok.com/@joyitas.conscientes?_t=8s4VFDx4qrl&_r=1" target="_blank" rel="noopener noreferrer" aria-label="TikTok" className="transition-transform">
                  <FontAwesomeIcon icon={faTiktok} size="lg" />
                </motion.a>
              </div>
            </div>

            {/* Legal Section */}
            <div className="flex flex-col items-start">
              <h3 className="text-lg font-semibold mb-4 text-text-dark dark:text-text-light">
                <Link to={localePath('/legal')} className="transition-transform">{t('footer.legal')}</Link>
              </h3>
              <nav className="flex flex-col space-y-2 text-text-dark dark:text-text-light text-left">
                <motion.div whileHover={{ scale: 1.1 }}>
                  <Link to={`${localePath('/legal')}#shipping`} className="transition-transform">{t('footer.shippingPolicy')}</Link>
                </motion.div>
                <motion.div whileHover={{ scale: 1.1 }}>
                  <Link to={`${localePath('/legal')}#returns`} className="transition-transform">{t('footer.returnsPolicy')}</Link>
                </motion.div>
                <motion.div whileHover={{ scale: 1.1 }}>
                  <Link to={`${localePath('/legal')}#terms`} className="transition-transform">{t('footer.termsConditions')}</Link>
                </motion.div>
                <motion.div whileHover={{ scale: 1.1 }}>
                  <Link to={`${localePath('/legal')}#privacy`} className="transition-transform">{t('footer.privacyPolicy')}</Link>
                </motion.div>
              </nav>
            </div>
          </div>

          {/* Copyright Section */}
          <div className="mt-8 pt-8 border-t border-gray-700 text-center text-text-dark dark:text-text-light text-sm">
            <p>{t('footer.copyright', { year: new Date().getFullYear() })}</p>
          </div>
        </div>

      </div>
    </footer>
  );
};

export default Footer;
